import React, { useRef, useState } from 'react';
import { Maybe, SaleCalendar } from '@gql/schema';
import {
  RoomAndTravellerSection,
  RoomTravellerSelectionVariant,
  TravellersSelection,
  createQueryParamFromSelection,
} from '@secretescapes/cauldron';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Panel,
  Popover,
  PopoverPosition,
  Scale,
  SelectOption,
  SelectVariant,
  css,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { useClickOutsideDetector } from '@utils/EventListeners.utils';
import { formatMomentToShortenedString } from '@utils/dates.utils';
import { setURLParams } from '@utils/url.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useTranslations } from '@utils/useTranslations';
import moment from 'moment';
import { ICapacity, IFlightState } from '@features/Flights';
import { FocusedInput, TFocusedInput } from '../AvailabilityPresentation.interfaces';
import { ALL_OFFERS, setCalendarURLParams } from '../AvailabilityPresentation.utils';
import { Calendar, CalendarSummary } from '../Calendar';
import { CalendarDates, NullableMoment } from '../Calendar/Calendar.interfaces';
import { LoadingSkeletonSection } from '../LoadingSkeleton/LoadingSkeleton';
import { Overlay } from '../Overlay';
import { useSelectionActions, useSelectionState } from '../SelectionContext';
import {
  Bar,
  CalendarActions,
  CalendarContainer,
  CalendarInputs,
  CalendarOverlayContainer,
  CalendarOverlayContent,
  DateInputsSkeleton,
  DropdownInputSkeleton,
  DropdownWrapper,
  Inputs,
  PanelWrapper,
  Selection,
  SelectionBarWrapper,
  StyledCustomSelect,
} from './AvailabilitySearchBar.styles';
import { determineRoomNumbersTitle } from './AvailabilitySearchBar.utils';
import { CustomSelect } from './CustomSelect';
import { DateInputs } from './DateInputs';
import { DropdownInput, Option } from './DropdownInput';
import { PanelInput } from './PanelInput/PanelInput';

interface Props {
  availability: Maybe<SaleCalendar>;
  checkInDate: NullableMoment;
  checkOutDate: NullableMoment;
  offerOptions: Option[];
  onDatesChange: (checkInDate: NullableMoment, checkOutDate: NullableMoment) => void;
  roomType: string;
  roomTypePlural: string;
  numOfRooms: number;
  setNumOfRooms: (roomNumber: number) => void;
  focusedInput: TFocusedInput;
  setFocusedInput: (input: FocusedInput | null) => void;
  onCalendarCancelled: () => void;
  availabilityLoading: boolean;
  withTabs: boolean;
  withFlights: boolean;
  isSameDates: boolean | undefined;
  flightState: IFlightState;
  onFlightStateChange: (arg: IFlightState) => void;
  roomCapacity: ICapacity;
  getFlightOptions: (airportId: string, durationId: number) => Promise<void>;
  scrollToCalendar: ((isStickyNavInactive?: boolean) => void) | undefined;
  animateOffersSelect: boolean;
  onAnimationChange: (value: boolean) => void;
}

export const AvailabilitySearchBar = ({
  availability,
  onDatesChange,
  checkInDate,
  checkOutDate,
  isSameDates,
  offerOptions,
  roomType,
  roomTypePlural,
  numOfRooms,
  setNumOfRooms,
  focusedInput,
  setFocusedInput,
  onCalendarCancelled,
  availabilityLoading,
  withTabs,
  withFlights,
  flightState,
  onFlightStateChange,
  roomCapacity,
  getFlightOptions,
  scrollToCalendar,
  animateOffersSelect,
  onAnimationChange,
}: Props) => {
  const isInputDisabled = !roomType; // If no offers, then no roomType

  const { setFinalRates } = useAppState();
  const { translations } = useTranslations();
  const { territoryLocale, isMobile } = useApplicationState();
  const travellersHOSelectionEnabled = useFeatureFlag(BullfinchFeatureFlags.travellersHOSelection);

  const isRelevantTravellersSelectionEnabled =
    withFlights || (!withFlights && travellersHOSelectionEnabled);

  const { offerId, travellersSelection } = useSelectionState();
  const { changeOfferId, changeTravellersSelection } = useSelectionActions();

  const [isAirportPanelOpen, setAirportPanelOpen] = useState(false);
  const [isRoomPanelOpen, setRoomsPanelOpen] = useState(false);

  const calendarRef = useRef<HTMLDivElement | null>(null);
  const dateInputRef = useRef<HTMLDivElement | null>(null);

  const roomsLabel = roomTypePlural.charAt(0).toUpperCase() + roomTypePlural.slice(1);
  const airportLabel = i18n('common.departureAirport.label', [], translations);
  const durationLabel = i18n('common.duration.label', [], translations);
  const offersLabel = i18n('calendar.offer.dropdown.label', [], translations);
  const checkInLabel = i18n('common.checkIn.label', [], translations);
  const checkOutLabel = i18n('common.checkOut.label', [], translations);
  const dateInputPlaceholder = i18n('search.dates.placeholder', [], translations);
  const resetDatesLabel = i18n('search.dates.reset', [], translations);
  const confirmDatesLabel = i18n('common.date.confirm', [], translations);

  const checkInMobileFormat = formatMomentToShortenedString(checkInDate?.locale(territoryLocale));
  const checkOutMobileFormat = formatMomentToShortenedString(checkOutDate?.locale(territoryLocale));
  const roomsSelectionText = determineRoomNumbersTitle(numOfRooms, roomType, roomTypePlural);

  const roomOptions: Option[] = [...new Array(roomCapacity.rooms).keys()].map(room => ({
    value: (room + 1).toString(),
    text: determineRoomNumbersTitle(room + 1, roomType, roomTypePlural),
  }));

  const airportOptions: Option[] = flightState.airports.map(airport => ({
    value: airport.id as string,
    text: airport.title,
    subtext: airport.price,
  }));

  const durationOptions: Option[] = flightState.nights.map(night => ({
    value: night.id as string,
    text: night.title,
    subtext: night.price,
  }));

  const closeCalendar = () => setFocusedInput(null);

  const closeCalendarOverlay = () => {
    closeCalendar();
    onCalendarCancelled();
  };

  useClickOutsideDetector(
    [calendarRef],
    () => {
      if (!isMobile) {
        closeCalendar();
        onCalendarCancelled();
      }
    },
    [dateInputRef]
  );

  const handleDatesChange = ({ startDate, endDate }: CalendarDates) => {
    onDatesChange(startDate ?? null, endDate ?? null);
  };

  const handleFocusChange = (input: TFocusedInput) => {
    if (isMobile) {
      setFocusedInput(input ?? FocusedInput.EndDate);
    } else if (input) {
      setFocusedInput(input);
    }
  };

  const handleDateInputAction = (input: TFocusedInput) => {
    setFocusedInput(input);

    if (!focusedInput) {
      if (!performance.getEntriesByName('start-render')?.length) {
        performance.mark('start-render');
      }

      if (!isMobile) {
        scrollToCalendar?.();
      }
    }
  };

  const handleResetClick = () => {
    changeOfferId(ALL_OFFERS);
    handleDatesChange({ startDate: undefined, endDate: undefined });
    handleFocusChange(FocusedInput.StartDate);
    setFinalRates(prev => ({
      ...prev,
      show: false,
      cheapestFinalRate: '',
      cheapestFinalRatePerRoomPerNight: '',
      numberOfTravellers: 2,
      numberOfNights: 1,
    }));
    setCalendarURLParams({ startDate: undefined, endDate: undefined });

    if (isMobile) {
      scrollToCalendar?.(false);
    }
  };

  const handleRoomSelect = (roomNumber: string) => {
    setNumOfRooms(Number(roomNumber));

    if (isMobile) {
      setRoomsPanelOpen(false);
    }
    setURLParams('rooms', roomNumber);
  };

  const handleAirportSelect = (newValue: string) => {
    const newAirport = flightState.airports.find(airport => airport.id === newValue);

    if (newAirport) {
      onFlightStateChange({
        ...flightState,
        selectedAirport: newAirport,
      });
      void getFlightOptions(newAirport.id as string, flightState.selectedNights.id as number);
      setURLParams('airportCode', newValue);
      setURLParams('airportName', newAirport?.title);
    }

    if (isMobile) {
      setAirportPanelOpen(false);
    }
  };

  const handleDurationSelect = (newValue: string) => {
    const newDuration = flightState.nights.find(night => night.id === newValue);

    if (newDuration) {
      onFlightStateChange({
        ...flightState,
        selectedNights: newDuration,
      });
      void getFlightOptions(flightState.selectedAirport.id as string, newDuration.id as number);
      setURLParams('duration', newValue);
      // Update checkOutDate to match new duration when dates selected.
      if (
        newDuration.id &&
        checkInDate &&
        checkOutDate &&
        moment(checkOutDate).startOf('d').diff(moment(checkInDate).startOf('d'), 'd') !==
          (newDuration.id as number)
      ) {
        const newCheckOutDate = moment(checkInDate).add(newDuration.id as number, 'd');
        onDatesChange(checkInDate, newCheckOutDate);
        setCalendarURLParams({
          startDate: checkInDate,
          endDate: newCheckOutDate,
        });
      }
    }
  };

  const handleCtaClick = () => {
    closeCalendar();
    setCalendarURLParams({
      startDate: checkInDate,
      endDate: checkOutDate,
    });
    changeOfferId(ALL_OFFERS);

    if (isMobile) {
      scrollToCalendar?.(true);
    }
  };

  const handleTravellersSelectionConfirm = (selection: TravellersSelection) => {
    changeTravellersSelection(selection);
    setURLParams('travellersSelection', createQueryParamFromSelection(selection));
  };

  if (isMobile) {
    return (
      <>
        <SelectionBarWrapper>
          {/* Airport Input */}
          {withFlights && (
            <div>
              <PanelInput
                label={airportLabel}
                value={flightState.selectedAirport.title}
                onClick={() => setAirportPanelOpen(true)}
                isDisabled={isInputDisabled}
              />
              <PanelWrapper removeExtraPadding={true}>
                <Panel
                  title={airportLabel}
                  isOpen={isAirportPanelOpen}
                  onClose={() => setAirportPanelOpen(false)}
                  fullScreen={true}
                  dataAttributes={{ testid: 'selection-bar-panel' }}
                >
                  <DropdownWrapper>
                    {airportOptions.map(option => (
                      <SelectOption
                        key={option.text}
                        title={option.text}
                        subtitle={option.subtext ?? ''}
                        onClick={() => handleAirportSelect(option.value)}
                      />
                    ))}
                  </DropdownWrapper>
                </Panel>
              </PanelWrapper>
            </div>
          )}
          {/* Rooms Input */}
          {!isRelevantTravellersSelectionEnabled && (
            <div>
              <PanelInput
                label={roomsLabel}
                value={roomsSelectionText}
                onClick={() => setRoomsPanelOpen(true)}
              />
              <PanelWrapper removeExtraPadding={true}>
                <Panel
                  title={roomsLabel}
                  onClose={() => setRoomsPanelOpen(false)}
                  isOpen={isRoomPanelOpen}
                  dataAttributes={{ testid: 'selection-bar-panel' }}
                  fullScreen={true}
                >
                  <DropdownWrapper>
                    {roomOptions.map(option => (
                      <SelectOption
                        key={option.text}
                        title={option.text}
                        onClick={() => handleRoomSelect(option.value)}
                      />
                    ))}
                  </DropdownWrapper>
                </Panel>
              </PanelWrapper>
            </div>
          )}
          <DateInputs
            dateInputRef={dateInputRef}
            checkInLabel={checkInLabel}
            checkOutLabel={checkOutLabel}
            checkInValue={checkInMobileFormat}
            checkOutValue={checkOutMobileFormat}
            placeholderText={dateInputPlaceholder}
            focusedInput={focusedInput}
            onAction={handleDateInputAction}
            isDisabled={isInputDisabled}
          />
          {/* Travellers Input */}
          {isRelevantTravellersSelectionEnabled && (
            <RoomAndTravellerSection
              variant={RoomTravellerSelectionVariant.TOGGLE_OVERLAY}
              onConfirm={handleTravellersSelectionConfirm}
              selection={travellersSelection}
              maxCapacityInfo={roomCapacity}
            />
          )}
        </SelectionBarWrapper>
        <Overlay isOpen={!!focusedInput} onClose={closeCalendarOverlay}>
          <CalendarOverlayContent>
            <Selection>
              <StyledCustomSelect
                label={offersLabel}
                options={offerOptions}
                value={offerId}
                variant={SelectVariant.panel}
                onSelect={changeOfferId}
              />
              {withFlights && (
                <StyledCustomSelect
                  label={`${durationLabel}:`}
                  options={durationOptions}
                  value={flightState.selectedNights.id as number}
                  variant={SelectVariant.panel}
                  onSelect={handleDurationSelect}
                />
              )}
            </Selection>
            <CalendarOverlayContainer>
              <Calendar
                availability={availability}
                availabilityLoading={availabilityLoading}
                startDate={checkInDate}
                endDate={checkOutDate}
                focusedInput={focusedInput}
                onDatesChange={handleDatesChange}
                onFocusChange={handleFocusChange}
                withFlights={withFlights}
                rooms={
                  isRelevantTravellersSelectionEnabled
                    ? travellersSelection.rooms.length
                    : numOfRooms
                }
              />
            </CalendarOverlayContainer>
          </CalendarOverlayContent>
          {checkInDate && checkOutDate && (
            <CalendarSummary
              isSameDates={!!isSameDates}
              onResetClick={handleResetClick}
              onConfirmClick={handleCtaClick}
            />
          )}
        </Overlay>
      </>
    );
  }

  return (
    <Bar withTabs={withTabs}>
      <Popover
        className="popover__parent"
        content={
          <CalendarContainer ref={calendarRef}>
            <CalendarInputs>
              <CustomSelect
                label={offersLabel}
                options={offerOptions}
                value={offerId}
                variant={SelectVariant.dropdown}
                animate={animateOffersSelect}
                fixedWidth={true}
                onSelect={changeOfferId}
                onAnimationEnd={() => onAnimationChange(false)}
              />

              {withFlights && (
                <CustomSelect
                  label={`${durationLabel}:`}
                  options={durationOptions}
                  value={flightState.selectedNights.id as number}
                  variant={SelectVariant.dropdown}
                  fixedWidth={true}
                  onSelect={handleDurationSelect}
                />
              )}
            </CalendarInputs>
            <Calendar
              onDatesChange={handleDatesChange}
              onFocusChange={handleFocusChange}
              startDate={checkInDate}
              endDate={checkOutDate}
              focusedInput={focusedInput}
              availability={availability}
              availabilityLoading={availabilityLoading}
              withFlights={withFlights}
              rooms={
                isRelevantTravellersSelectionEnabled ? travellersSelection.rooms.length : numOfRooms
              }
            />
            <div>
              <CalendarActions>
                <Button
                  dataAttributes={{ testid: 'reset-dates' }}
                  onClick={handleResetClick}
                  size={ButtonSize.medium}
                  variant={ButtonVariant.secondary}
                  disabled={!checkInDate && !checkOutDate}
                >
                  {resetDatesLabel}
                </Button>
                <Button
                  dataAttributes={{ testid: 'confirm-dates' }}
                  onClick={handleCtaClick}
                  disabled={!checkInDate || !checkOutDate || isSameDates}
                  size={ButtonSize.medium}
                >
                  {confirmDatesLabel}
                </Button>
              </CalendarActions>
            </div>
          </CalendarContainer>
        }
        noPadding={true}
        open={!!focusedInput}
        position={PopoverPosition.bottom}
        showPopoverArrow={false}
        triggerStyles={css`
          width: unset;
        `}
        withMaxWidth={false}
        zIndex={3}
      >
        <Inputs
          withFlights={withFlights}
          travellersHOSelectionEnabled={travellersHOSelectionEnabled}
        >
          {!isRelevantTravellersSelectionEnabled && (
            <DropdownInput
              label={roomsLabel}
              options={roomOptions}
              value={
                roomOptions.find(room => room.value === numOfRooms.toString())?.text ??
                roomOptions[0]?.text
              }
              onSelect={handleRoomSelect}
            />
          )}
          {withFlights && (
            <DropdownInput
              label={airportLabel}
              options={airportOptions}
              value={flightState.selectedAirport.title}
              onSelect={handleAirportSelect}
              isDisabled={isInputDisabled}
            />
          )}
          <DateInputs
            dateInputRef={dateInputRef}
            checkInLabel={checkInLabel}
            checkOutLabel={checkOutLabel}
            checkInValue={checkInDate?.format('dddd LL') ?? ''}
            checkOutValue={checkOutDate?.format('dddd LL') ?? ''}
            placeholderText={dateInputPlaceholder}
            focusedInput={focusedInput}
            onAction={handleDateInputAction}
            isDisabled={isInputDisabled}
          />
          {/* Travellers Input */}
          {isRelevantTravellersSelectionEnabled && (
            <RoomAndTravellerSection
              variant={RoomTravellerSelectionVariant.TOGGLE_SELECT}
              onConfirm={handleTravellersSelectionConfirm}
              selection={travellersSelection}
              maxCapacityInfo={roomCapacity}
            />
          )}
        </Inputs>
      </Popover>
    </Bar>
  );
};

interface SkeletonProps {
  withTabs: boolean;
  isMobile?: boolean;
}

const Skeleton = ({ withTabs, isMobile }: SkeletonProps) => {
  return (
    <Bar data-testid="search-bar-skeleton" withTabs={withTabs} isMobile={isMobile}>
      <DropdownInputSkeleton>
        <LoadingSkeletonSection height={isMobile ? '58px' : '72px'} borderRadius={Scale.xxs} />
      </DropdownInputSkeleton>
      <DateInputsSkeleton>
        <LoadingSkeletonSection height={isMobile ? '58px' : '72px'} borderRadius={Scale.xxs} />
      </DateInputsSkeleton>
    </Bar>
  );
};

AvailabilitySearchBar.Skeleton = Skeleton;
